import { type Dispatch, type SetStateAction } from 'react';

import { ArrowLeftOutlined, CheckCircleOutlined, RollbackOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, Modal, Tag } from 'antd';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale/ru';

import { useGetTestingParticipantQuery, EApplicationStatus } from '@/api/endpoints/schools/schoolSelectionPanel';
import { ApplicationDataSection } from '@/pages/(publicPages)/(schools)/(schoolSelectionPanel)/ApplicationsTab/(pages)/_components/ApplicationDataSection/ApplicationDataSection';
import { CANDIDATE_STATUS_TO_TAG_SETTINGS } from '@/pages/(publicPages)/(schools)/(schoolSelectionPanel)/ApplicationsTab/consts';
import { TEST_STATUS_TO_TAG_SETTINGS } from '@/pages/(publicPages)/(schools)/(schoolSelectionPanel)/TestingTab/(pages)/TestingParticipantsPage/consts';
import { FetchDataWrapper } from '@/shared/components';
import { DATE_FORMATTERS } from '@/shared/consts';
import { formatCandidateApplicationSurveyData, toggleSetItem } from '@/shared/helpers';
import { ETestStatus } from '@/shared/types';

import styles from './SurveyResultsModal.module.scss';

interface IProp {
  isModalOpen: boolean;
  handleCancel: VoidFunction;
  schoolId: number;
  selectedUserId: number;
  mainListUsersIds: Set<number>;
  setMainListUsersIds: Dispatch<SetStateAction<Set<number>>>;
}

export const SurveyResultsModal = ({
  isModalOpen,
  handleCancel,
  schoolId,
  selectedUserId,
  mainListUsersIds,
  setMainListUsersIds,
}: IProp) => {
  const {
    data: testingParticipantData,
    isFetching: isTestingParticipantFetching,
    isError: isTestingParticipantError,
    refetch,
  } = useGetTestingParticipantQuery({
    schoolId,
    participantId: selectedUserId,
  });
  const isNotInReserve = mainListUsersIds.has(selectedUserId);

  const handleToggleReserve = () => {
    setMainListUsersIds((prev) => toggleSetItem(prev, selectedUserId));
  };

  return (
    <Modal
      width={600}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <FetchDataWrapper
        isModalUse
        isLoading={isTestingParticipantFetching}
        isError={isTestingParticipantError}
        refreshCb={refetch}
      >
        {testingParticipantData &&
          (() => {
            const {
              user: { full_name: fullName },
              admission_application: { created_at: createdAt, ...admissionApplication },
            } = testingParticipantData;
            const applicationSurveyData = formatCandidateApplicationSurveyData({
              user: testingParticipantData.user,
              ...admissionApplication,
            });

            return (
              <>
                <Button
                  type="link"
                  className={styles['back-btn']}
                  icon={<ArrowLeftOutlined />}
                  onClick={handleCancel}
                >
                  Назад к списку
                </Button>

                <div className={styles['main-info-wrapper']}>
                  <div className={styles['name-wrapper']}>
                    <p className={styles.name}>{fullName}</p>
                    <Tag
                      icon={<CheckCircleOutlined />}
                      color={CANDIDATE_STATUS_TO_TAG_SETTINGS[EApplicationStatus.Approved].color}
                    >
                      {TEST_STATUS_TO_TAG_SETTINGS[ETestStatus.TestPassed].text}
                    </Tag>
                  </div>
                  <p className={styles['submission-date']}>
                    Заявка от{' '}
                    {format(createdAt, DATE_FORMATTERS.DAY_FULL_MONTH_FULL_YEAR_SEPARATOR_HOURS_MINUTES, {
                      locale: ru,
                    })}
                  </p>
                  <Button
                    icon={isNotInReserve ? <TeamOutlined /> : <RollbackOutlined />}
                    color={isNotInReserve ? 'volcano' : 'green'}
                    variant="solid"
                    onClick={handleToggleReserve}
                  >
                    {isNotInReserve ? 'Перевести в резерв' : 'Вывести из резерва'}
                  </Button>
                </div>
                <div className={styles['contact-wrapper']}>
                  <p className={styles['section-title']}>Контактная информация</p>
                  <div className={styles['contact-info-wrapper']}>
                    {applicationSurveyData.slice(0, 2).map(({ id, icon, title, dataValue }) => (
                      <ApplicationDataSection
                        key={id}
                        className={styles['contact-data-section']}
                        icon={icon}
                        title={title}
                        dataValue={dataValue}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <p className={styles['section-title']}>Ответы на опрос</p>
                  <div className={styles['survey-data-wrapper']}>
                    {applicationSurveyData.slice(2).map(({ id, icon, title, dataValue, theme }) => (
                      <ApplicationDataSection
                        key={id}
                        className={styles['survey-data-section']}
                        icon={icon}
                        title={title}
                        dataValue={dataValue}
                        theme={theme}
                      />
                    ))}
                  </div>
                </div>
              </>
            );
          })()}
      </FetchDataWrapper>
    </Modal>
  );
};
